/**
 * A file, read in the app instead of downloaded.
 *
 * ── One shell, four bodies ───────────────────────────────────────────────────
 * The shell is the transcript viewer's (`size="reading"`): a full-screen sheet below 840px, an
 * 1100px centred panel above it, header and footer pinned and the file scrolling between them.
 * The two dialogs are deliberately one family — a person opening a recording and a person
 * opening a PDF are doing the same thing, and a header that states the file over a line of
 * labelled pairs is how both say what they are showing.
 *
 * Which body is drawn was decided before this opened, by `resolveDocumentOpenAction`: this
 * component never asks whether a file is previewable, only which of the four it was told to
 * draw.
 *
 * ── The footer is the way out of every refusal ───────────────────────────────
 * Download sits beside Close on every body, whatever the body managed to draw. Preview replaces
 * the *default* action on a row, never the ability to get the file, so a text file past the cap
 * and a fetch that did not land both end at a button that works.
 *
 * ── Nothing is read into the page but text ───────────────────────────────────
 * The image is a `src` attribute and the PDF's bytes go to pdf.js's worker; neither passes
 * through this file. Only Markdown and plain text are read into the page, because only they have
 * to be decoded before they can be laid out — and only up to the cap the view-model states.
 */

import { DownloadIcon, XIcon } from "lucide-react";
import { Fragment, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { LoadingPanel } from "@/components/ui/loading-state";
import { type DocumentPreviewKind, getFileKind, getFileTypeIcon } from "@/lib/file-types";
import { cn } from "@/lib/utils";
import { useTextPreview } from "../hooks/use-text-preview";
import {
  buildDocumentPreviewMeta,
  DOCUMENT_PREVIEW_META_ABSENT_LABEL,
  previewKindIsFramed,
  previewKindReadsText,
  resolveTextPreviewFallback,
  type TextPreviewState,
} from "../lib/document-preview-view";
import { ImagePreviewBody } from "./preview-bodies/image-preview-body";
import { MarkdownPreviewBody } from "./preview-bodies/markdown-preview-body";
import { PdfPreviewBody } from "./preview-bodies/pdf-preview-body";
import { PreviewFallbackBody } from "./preview-bodies/preview-fallback-body";
import { TextPreviewBody } from "./preview-bodies/text-preview-body";

export interface DocumentPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Which body to draw. The resolver picked it; this component does not re-decide it. */
  previewKind: DocumentPreviewKind;
  /** The signed storage URL — the bytes the body draws and the footer hands over. */
  url: string;
  /** The name a person reads, which a rename moves. */
  name: string;
  /** The name the bytes arrived under: the extension in the header, and the saved filename. */
  fileName: string;
  mimeType: string | null;
  sizeBytes: number | null;
  uploadedAt: number | null;
  /** The clock the "Uploaded" pair reads its distance against. */
  now: number;
}

export function DocumentPreviewDialog({
  open,
  onOpenChange,
  previewKind,
  url,
  name,
  fileName,
  mimeType,
  sizeBytes,
  uploadedAt,
  now,
}: DocumentPreviewDialogProps) {
  const Icon = getFileTypeIcon(getFileKind(fileName, mimeType ?? undefined));
  const meta = useMemo(
    () => buildDocumentPreviewMeta({ fileName, sizeBytes, uploadedAt }, now),
    [fileName, now, sizeBytes, uploadedAt]
  );

  const text = useTextPreview({
    url,
    sizeBytes,
    enabled: open && previewKindReadsText(previewKind),
  });
  const isFramed = previewKindIsFramed(previewKind);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="document-preview-dialog" showCloseButton={false} size="reading">
        <DialogHeader className="flex shrink-0 flex-row items-start justify-between gap-3 border-border border-b px-5 pt-4 pb-3 min-[840px]:px-6 min-[840px]:pt-[18px] min-[840px]:pb-4">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <DialogTitle className="flex min-w-0 items-center gap-2 font-semibold text-base">
              <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              <span className="truncate">{name}</span>
            </DialogTitle>
            {meta.length === 0 ? (
              <DialogDescription data-testid="document-preview-meta">
                {DOCUMENT_PREVIEW_META_ABSENT_LABEL}
              </DialogDescription>
            ) : (
              <DialogDescription
                data-testid="document-preview-meta"
                render={<dl />}
                className="flex flex-wrap items-center gap-2.5"
              >
                {meta.map((pair, index) => (
                  <Fragment key={pair.key}>
                    {index === 0 ? null : (
                      <span aria-hidden className="h-2.5 w-px shrink-0 bg-border" />
                    )}
                    <div data-meta={pair.key} className="flex items-center gap-[5px]">
                      <dt className="font-normal text-[0.71875rem]/[1.5] text-neutral-dim">
                        {pair.label}
                      </dt>
                      <dd className="font-medium text-[0.71875rem]/[1.5] text-neutral-muted-fg">
                        {pair.value}
                      </dd>
                    </div>
                  </Fragment>
                ))}
              </DialogDescription>
            )}
          </div>
          <DialogClose
            data-testid="document-preview-close"
            render={<Button variant="ghost" size="icon-sm" className="-mt-1 shrink-0" />}
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>

        {/* A framed body sets its size through flex-basis, not height: the panel is a flex column
            that is only as tall as its content, and a flex item's `height` is overridden by its
            zero basis there, collapsing the body to nothing. The basis gives the frame 70vh to
            fill and still shrinks under the panel's max height. */}
        <div
          data-testid="document-preview-body"
          className={cn(
            "min-h-0 flex-1",
            isFramed ? "overflow-hidden min-[840px]:basis-[70vh]" : "overflow-y-auto"
          )}
        >
          <PreviewBody previewKind={previewKind} url={url} name={name} text={text} />
        </div>

        <div className="flex shrink-0 flex-col gap-2.5 border-border border-t px-5 pt-3 pb-4 min-[840px]:flex-row min-[840px]:items-center min-[840px]:justify-end min-[840px]:gap-2.5 min-[840px]:px-6 min-[840px]:py-3">
          <Button
            variant="outline"
            data-testid="document-preview-cancel"
            className="h-11 flex-1 min-[840px]:h-8 min-[840px]:flex-none"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
          {/* A link, not a command: the browser saves the bytes under the name the file arrived
              with, whatever the row has since been renamed to — the same anchor the ⋯ menu is. */}
          <Button
            data-testid="document-preview-download"
            className="h-11 flex-1 min-[840px]:h-8 min-[840px]:flex-none"
            render={
              <a href={url} download={fileName} target="_blank" rel="noopener noreferrer">
                <DownloadIcon className="size-3.5" aria-hidden />
                Download
              </a>
            }
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PreviewBody({
  previewKind,
  url,
  name,
  text,
}: {
  previewKind: DocumentPreviewKind;
  url: string;
  name: string;
  text: TextPreviewState;
}) {
  if (previewKind === "image") {
    return <ImagePreviewBody url={url} name={name} className="h-full w-full p-4" />;
  }
  if (previewKind === "pdf") {
    return <PdfPreviewBody url={url} name={name} />;
  }

  if (text.kind === "loading") {
    return <LoadingPanel className="h-full min-h-40" data-testid="document-preview-loading" />;
  }

  const fallback = resolveTextPreviewFallback(text);
  if (fallback !== null) {
    return <PreviewFallbackBody fallback={fallback} />;
  }

  const body = text.kind === "ready" ? text.text : "";
  return previewKind === "markdown" ? (
    <MarkdownPreviewBody text={body} />
  ) : (
    <TextPreviewBody text={body} />
  );
}
